import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { getTarjetas, postTarjeta, putTarjeta, deleteTarjeta } from '../../../services/tarjetas.service';
import { getEnlaces, postEnlace, putEnlace, deleteEnlace } from '../../../services/enlaces.service';
import { getTiposDeAccesos } from '../../../services/tiposDeAccesos.service';

export const areaOptions = [
  { id: 'ADM', label: 'Administradores' },
  { id: 'GA', label: 'Gestión Académica' },
  { id: 'LOG', label: 'Logística' },
  { id: 'ROOT', label: 'Subdirección' },
];

const normalize = (text) =>
  String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export const useLinksGestor = () => {
  const { user } = useAuth();
  const userRol = user?.rol || 'ADM';
  const isSub = userRol === 'ROOT';

  const permittedAreas = useMemo(
    () => (isSub ? areaOptions : areaOptions.filter((item) => item.id === userRol)),
    [isSub, userRol],
  );

  const [query, setQuery] = useState('');
  const [section, setSection] = useState('todas');
  const [area, setAreaState] = useState(() => (isSub ? 'ADM' : userRol));
  const [editMode, setEditMode] = useState(false);

  const [sectionsData, setSectionsData] = useState([]);
  const [tiposDeAccesos, setTiposDeAccesos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sincronizar el área activa cuando cambia el usuario autenticado
  useEffect(() => {
    if (user?.rol) {
      setAreaState(user.rol === 'ROOT' ? 'ADM' : user.rol);
    }
  }, [user?.rol]);

  const resolveTipoAccesoId = useCallback(
    (reqValue, explicitId) => {
      if (explicitId) return Number(explicitId);
      const cleanReq = String(reqValue || 'Ninguno').trim().toLowerCase();
      const found = tiposDeAccesos.find((t) => t.nombre.trim().toLowerCase() === cleanReq);
      if (found) return Number(found.id);
      const fallbackNinguno = tiposDeAccesos.find((t) => t.nombre.trim().toLowerCase() === 'ninguno');
      return fallbackNinguno ? Number(fallbackNinguno.id) : Number(tiposDeAccesos[0]?.id || 1);
    },
    [tiposDeAccesos],
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [tarjetasData, enlacesData, tiposData] = await Promise.all([
        getTarjetas({ rol_cod: area }),
        getEnlaces(),
        getTiposDeAccesos(),
      ]);

      const safeTarjetas = Array.isArray(tarjetasData) ? tarjetasData : [];
      const safeEnlaces = Array.isArray(enlacesData) ? enlacesData : [];
      const safeTipos = Array.isArray(tiposData) ? tiposData : [];

      setTiposDeAccesos(safeTipos);

      const mappedSections = safeTarjetas.map((tarjeta) => {
        const cardEnlaces = safeEnlaces.filter((e) => Number(e.tarjeta_id) === Number(tarjeta.id));
        return {
          id: tarjeta.id,
          title: tarjeta.nombre,
          rol_cod: tarjeta.rol_cod,
          icon: '',
          links: cardEnlaces.map((e) => {
            const tipoNombre =
              e.detalle_tipo_acceso?.nombre ||
              safeTipos.find((t) => Number(t.id) === Number(e.tipo_acceso_id))?.nombre ||
              'Ninguno';
            const reqClean = tipoNombre === 'Ninguno' ? '' : tipoNombre;
            const isVpnOrForti =
              tipoNombre.toLowerCase().includes('vpn') || tipoNombre.toLowerCase().includes('forti');

            return {
              id: e.id,
              name: e.nombre,
              url: e.url || '#',
              test: Boolean(e.es_entorno_test),
              tipo_acceso_id: e.tipo_acceso_id,
              req: reqClean,
              requiresVPN: isVpnOrForti,
            };
          }),
        };
      });

      setSectionsData(mappedSections);
    } catch (err) {
      console.error('Error al cargar los datos del gestor de enlaces:', err);
      setError(err.message || 'No se pudieron cargar los enlaces desde el servidor.');
    } finally {
      setLoading(false);
    }
  }, [area]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredData = useMemo(() => {
    const q = normalize(query);
    const words = q.split(/\s+/).filter(Boolean);
    return sectionsData.map((sec) => ({
      ...sec,
      links: (sec.links || []).filter((link) => {
        const hay = normalize(
          `${sec.title} ${link.name} ${link.test ? 'test prueba' : ''} ${link.req || ''} ${link.requiresVPN ? 'vpn forti' : ''}`,
        );
        return words.every((word) => hay.includes(word));
      }),
    }));
  }, [query, sectionsData]);

  const shownSections = useMemo(() => {
    const q = normalize(query);
    const words = q.split(/\s+/).filter(Boolean);
    return filteredData
      .filter((item) => section === 'todas' || String(section) === String(item.id))
      // Cuando hay búsqueda, ocultar tarjetas cuyo título no coincide y que tampoco tienen enlaces coincidentes.
      .filter((item) => !q || words.every((w) => normalize(item.title).includes(w)) || item.links.length > 0)
      .map((item) => ({
        ...item,
        links: item.links.map((link) => ({
          ...link,
          hasReq: Boolean(link.req || link.requiresVPN),
          url: link.url || '#',
        })),
      }));
  }, [filteredData, section, query]);

  const navItems = useMemo(
    () => [
      { id: 'todas', label: 'Todas' },
      ...sectionsData.map((item) => ({ id: item.id, label: item.title })),
    ],
    [sectionsData],
  );

  const addSection = async ({ title }) => {
    const cleanTitle = String(title || '').trim();
    if (!cleanTitle) return;
    await postTarjeta({ nombre: cleanTitle, rol_cod: area });
    await fetchData();
  };

  const updateSection = async (sectionId, newTitle) => {
    const cleanTitle = String(newTitle || '').trim();
    if (!cleanTitle) return;
    await putTarjeta(sectionId, { nombre: cleanTitle, rol_cod: area });
    await fetchData();
  };

  const deleteSection = async (sectionId) => {
    await deleteTarjeta(sectionId);
    if (String(section) === String(sectionId)) {
      setSection('todas');
    }
    await fetchData();
  };

  const addLink = async (sectionId, link) => {
    const tipoAccesoId = resolveTipoAccesoId(link.req, link.tipo_acceso_id);
    await postEnlace({
      nombre: String(link.nombre ?? link.name ?? '').trim(),
      url: String(link.url || '').trim(),
      tarjeta_id: Number(link.tarjeta_id ?? sectionId),
      tipo_acceso_id: tipoAccesoId,
      es_entorno_test: Boolean(link.es_entorno_test ?? link.test),
    });
    await fetchData();
  };

  const updateLink = async (sectionId, indexOrLinkId, updatedLink) => {
    const sec = sectionsData.find((item) => String(item.id) === String(sectionId));
    const targetLink =
      sec?.links?.[indexOrLinkId] ||
      sec?.links?.find((l) => String(l.id) === String(indexOrLinkId)) ||
      updatedLink;
    const linkId = updatedLink?.id || targetLink?.id || indexOrLinkId;
    const tipoAccesoId = resolveTipoAccesoId(updatedLink.req, updatedLink.tipo_acceso_id);

    await putEnlace(linkId, {
      nombre: String(updatedLink.nombre ?? updatedLink.name ?? '').trim(),
      url: String(updatedLink.url || '').trim(),
      tipo_acceso_id: tipoAccesoId,
      es_entorno_test: Boolean(updatedLink.es_entorno_test ?? updatedLink.test),
    });
    await fetchData();
  };

  const deleteLink = async (sectionId, indexOrLinkId, linkObj) => {
    const sec = sectionsData.find((item) => String(item.id) === String(sectionId));
    const targetLink =
      linkObj ||
      sec?.links?.[indexOrLinkId] ||
      sec?.links?.find((l) => String(l.id) === String(indexOrLinkId));
    const linkId = targetLink?.id ?? indexOrLinkId;
    await deleteEnlace(linkId);
    await fetchData();
  };

  const reorderSections = (sourceId, targetId) => {
    setSectionsData((current) => {
      const from = current.findIndex((item) => String(item.id) === String(sourceId));
      const to = current.findIndex((item) => String(item.id) === String(targetId));
      if (from < 0 || to < 0 || from === to) return current;
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const reorderLinks = (sectionId, sourceIndex, targetIndex) => {
    setSectionsData((current) =>
      current.map((item) => {
        if (
          String(item.id) !== String(sectionId) ||
          sourceIndex === targetIndex ||
          sourceIndex < 0 ||
          targetIndex < 0 ||
          sourceIndex >= item.links.length ||
          targetIndex >= item.links.length
        ) {
          return item;
        }
        const links = [...item.links];
        const [moved] = links.splice(sourceIndex, 1);
        links.splice(targetIndex, 0, moved);
        return { ...item, links };
      }),
    );
  };

  const currentArea = areaOptions.find((item) => item.id === area);

  const setArea = (nextArea) => {
    if (!isSub && nextArea !== userRol) return;
    setAreaState(nextArea);
    setSection('todas');
    setQuery('');
    setEditMode(false);
  };

  const selectSection = (sectionId) => setSection(sectionId);

  const isAdmin = ['ROOT', 'ADM', 'GA', 'LOG'].includes(userRol);

  return {
    query,
    setQuery,
    section,
    selectSection,
    area,
    setArea,
    areaOptions: permittedAreas,
    currentAreaName: currentArea ? currentArea.label : '',
    isAdmin,
    isSub,
    navItems,
    shownSections,
    empty: shownSections.length === 0,
    loading,
    error,
    tiposDeAccesos,
    editMode,
    setEditMode,
    addSection,
    updateSection,
    deleteSection,
    addLink,
    updateLink,
    deleteLink,
    reorderSections,
    reorderLinks,
    refresh: fetchData,
  };
};

export default useLinksGestor;
